// AuthUi.jsx
import React, { useEffect, useState, useMemo } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Auth } from "@supabase/auth-ui-react";
import { ThemeSupa } from "@supabase/auth-ui-shared";
import { supabasePublic } from "../../../components/supabaseClient";
import { getRedirectURL, getBackendURL, basename } from "../../env";
import { setAuthContext, trackUserEvent, flushUserEvents } from "../../../utils/userEventTracker";
import { recordAuthEvent } from "../../../services/rlsAudit";
import HelpDeskButton from "../../HelpDeskButton";
import {
  PATIENT_ACCOUNT_TYPE,
  emptyPatientSignupFields,
  validatePatientSignupFields,
  buildUnauthenticatedSignupPayload,
  patientDisplayName,
} from "../patientSignupFields";
import {
  getActiveSchemaForRole,
  getTenantSchemaForAccountType,
  isPatientRole,
  resolveSignInAccountType,
} from "../../../config/roles";

const withTimeout = (promise, timeoutMs, message) => {
  let timeoutId;
  const timeout = new Promise((_, reject) => {
    timeoutId = setTimeout(() => reject(new Error(message)), timeoutMs);
  });
  return Promise.race([promise, timeout]).finally(() => clearTimeout(timeoutId));
};

const AuthUi = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const authView = location.state?.authView || "sign_in";
  const _sp = new URLSearchParams(location.search);
  const refParam = _sp.get("ref");
  const viewParam = _sp.get("view");
  const lockedReferralName = refParam ? decodeURIComponent(refParam) : null;

  const [isSignUp, setIsSignUp] = useState(authView === "sign_up" || viewParam === "sign_up" || !!refParam);
  const [username, setUsername] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [accountType, setAccountType] = useState("doctor");
  const [patientFields, setPatientFields] = useState(emptyPatientSignupFields);
  const [signupReason, setSignupReason] = useState("");
  const [referralSource, setReferralSource] = useState(lockedReferralName ? "a friend" : "Instagram");
  const [referralDetail, setReferralDetail] = useState(lockedReferralName || "");
  const [error, setError] = useState("");
  const isPatientSignup = accountType === PATIENT_ACCOUNT_TYPE;

  const referralNeedsDetail = referralSource === "a friend" || referralSource === "other";
  const [loading, setLoading] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [resetEmail, setResetEmail] = useState("");
  const [resetMessage, setResetMessage] = useState("");
  const [resetError, setResetError] = useState("");

  const customAppearance = {
    theme: ThemeSupa,
    variables: {
      default: {
        colors: {
          brand: "#4A90E2",
        },
        fontSizes: {
          baseBody: "16px",
        },
        radii: {
          borderRadiusButton: "12px",
        },
      },
    },
    style: {
      container: {
        borderRadius: "8px",
        padding: "20px",
      },
      label: {
        fontWeight: "bold",
      },
      input: {
        borderColor: "#4A90E2",
        borderRadius: "8px",
      },
      button: {
        backgroundColor: "#3b5998",
        borderRadius: "12px",
        padding: "10px",
        fontWeight: "bold",
        color: "#fff",
      },
    },
  };

  const inputStyle = {
    width: "100%",
    padding: "10px",
    borderRadius: "8px",
    border: "1px solid #4A90E2",
    fontSize: "16px",
    boxSizing: "border-box",
  };

  const labelStyle = {
    fontWeight: "bold",
    display: "block",
    marginBottom: "4px",
  };

  // Wrap supabase client so usernames (no @) are resolved to their real email
  const signInClient = useMemo(() => {
    const client = supabasePublic();
    const originalSignIn = client.auth.signInWithPassword.bind(client.auth);
    client.auth.signInWithPassword = async (credentials) => {
      if (credentials.email && !credentials.email.includes("@")) {
        console.log("[AUTH] Username detected (no @) — calling /api/resolve-username/ to look up email...");
        const controller = new AbortController();
        const timeoutId = setTimeout(() => {
          controller.abort();
          console.warn(
            "[AUTH] /api/resolve-username/ timed out after 10s.\n" +
            "The backend received the request but the Supabase query (SELECT email FROM users WHERE name=...) " +
            "didn't respond in time — the Supabase DB is under high load (check Monitor page: CPU/Disk I/O).\n" +
            "Falling back to treating the input as a direct email address."
          );
        }, 10000);
        try {
          const backendUrl = getBackendURL();
          const res = await fetch(`${backendUrl}/api/resolve-username/`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ username: credentials.email.trim() }),
            signal: controller.signal,
          });
          clearTimeout(timeoutId);
          if (res.ok) {
            const { email } = await res.json();
            console.log("[AUTH] Username resolved to email — proceeding with sign-in");
            credentials.email = email;
          } else {
            console.warn("[AUTH] /api/resolve-username/ returned", res.status, "— trying original input as email");
          }
          // If lookup fails, fall through with the original input — Supabase will return a normal auth error
        } catch (err) {
          clearTimeout(timeoutId);
          if (err.name === 'AbortError') {
            console.warn(
              "[AUTH] /api/resolve-username/ fetch aborted after 10s (AbortController fired).\n" +
              "The backend Supabase query took too long — DB CPU or Disk I/O is high.\n" +
              "Falling back to treating the input as a direct email address."
            );
          } else {
            console.error("[AUTH] /api/resolve-username/ fetch failed:", err.message);
          }
        }
      }
      console.log("[AUTH] Calling supabase.auth.signInWithPassword — waiting for Supabase auth response...");
      const result = await originalSignIn(credentials);
      if (result.error) {
        console.error("[AUTH] signInWithPassword failed:", result.error.message, result.error);
        recordAuthEvent("login", { success: false, email: credentials.email });
      } else {
        console.log("[AUTH] signInWithPassword succeeded — auth state change handler will redirect");
      }
      return result;
    };
    return client;
  }, []);

  // Redirect immediately if already signed in
  useEffect(() => {
    (async () => {
      const { data: { session } } = await supabasePublic().auth.getSession();
      if (!session?.user?.id) return;
      // Gate unapproved patients: an approved patient has patient_match_status
      // 'verified'. Anyone else with a patient role must go verify + await
      // approval — never straight into the portal.
      try {
        const { data: pr } = await supabasePublic()
          .from("users")
          .select("role, patient_match_status")
          .eq("id", session.user.id)
          .single();
        if (isPatientRole(pr?.role) && pr?.patient_match_status !== "verified") {
          console.log("⛔ [AUTH] Already signed in but patient not approved — redirecting to verification");
          window.location.href = window.location.origin + (basename || "") + "/auth/patient-verification";
          return;
        }
      } catch (e) {
        console.warn("[AUTH] patient approval check failed:", e?.message);
      }
      console.log("✅ [AUTH] Already signed in — redirecting to /schedules");
      window.location.href = window.location.origin + (basename || "") + "/schedules";
    })();
  }, []);

  // Change the email input to type="text" so browser doesn't block usernames
  useEffect(() => {
    const emailInput = document.querySelector('input[type="email"]');
    if (emailInput) emailInput.type = "text";
  });

  useEffect(() => {
    let cancelled = false;

    const handleSignedIn = async (session) => {
      if (!session?.user || cancelled) return;

      const user = session.user;
      console.log("👤 [SIGNIN] User signed in:", {
        user_id: user.id,
        email: user.email,
        email_confirmed: user.email_confirmed_at,
        username: user.user_metadata?.username,
        account_type: user.user_metadata?.account_type,
      });

      // Prefer role/schema from database (source of truth); fall back to auth user_metadata for new users
      const metadataAccountType = user.user_metadata?.account_type || "doctor";
      let accountType = resolveSignInAccountType(metadataAccountType, null);
      let dbUser = null;
      let dbSchema = null;
      try {
        const { data, error: dbError } = await withTimeout(
          supabasePublic()
            .from("users")
            .select("role, schema, patient_match_status")
            .eq("id", user.id)
            .maybeSingle(),
          5000,
          "[SIGNIN] users role/schema lookup timed out after 5s",
        );
        dbUser = data;
        if (!dbError && dbUser?.role) {
          const role = dbUser.role;
          accountType = resolveSignInAccountType(metadataAccountType, role);
          dbSchema = dbUser.schema || null;
          console.log("📋 [SIGNIN] Role/schema from DB:", { role, accountType, dbSchema });
        } else if (dbError) {
          console.warn("📋 [SIGNIN] DB lookup failed — using auth metadata account_type:", dbError);
        }
      } catch (err) {
        console.warn("📋 [SIGNIN] DB lookup failed — using auth metadata account_type:", err.message);
      }
      if (cancelled) return;

      if (
        accountType.toLowerCase() === "patient" &&
        sessionStorage.getItem("patient_signup_backend_pending") === "true"
      ) {
        console.log("⏳ [SIGNIN] Waiting for patient signup backend creation before redirecting");
        return;
      }
      if (accountType.toLowerCase() === "patient" && dbUser?.patient_match_status !== "verified") {
        console.log("⛔ [SIGNIN] Patient not approved (status:", dbUser?.patient_match_status, ") — redirecting to verification");
        window.location.href = window.location.origin + (basename || "") + "/auth/patient-verification";
        return;
      }
      const schemaName = getTenantSchemaForAccountType(accountType) || 'public';
      const schemaAlreadySet = dbSchema === schemaName;
      // Keep clinical roles on public, and tenant roles on their matching tenant schema.
      const activeSchema = getActiveSchemaForRole(dbUser?.role || accountType, dbSchema || schemaName);

      console.log("📋 [SIGNIN] Schema resolution:", { accountType, schemaName, dbSchema, schemaAlreadySet, activeSchema });

      // Always call assign-user-schema for tenant schema users (student/socialworker/other)
      // to ensure the tenant schema row exists. Even when schemaAlreadySet=true the DB trigger
      // may have populated public.users.schema without creating the row in the tenant schema.
      // Fire and forget — do NOT await. ProtectedRoute handles any brief PostgREST cache delay.
      if (schemaName !== 'public') {
        const backendUrl = getBackendURL();
        if (schemaAlreadySet) {
          console.log("🔄 [SIGNIN] Schema already set to", schemaName, "— firing assign-user-schema to ensure tenant row exists");
        } else {
          console.log("🗂️ [SIGNIN] First-time schema assignment needed — firing in background:", schemaName);
        }
        fetch(`${backendUrl}/api/assign-user-schema/`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ user_id: user.id, account_type: accountType }),
        })
          .then(r => r.json().catch(() => ({})))
          .then(result => {
            if (result?.status === 'success') {
              sessionStorage.setItem(`schema_created_${user.id}`, 'true');
              console.log("✅ [SIGNIN] Background schema assignment complete:", result);
            } else {
              console.warn("⚠️ [SIGNIN] Background schema assignment returned:", result);
            }
          })
          .catch(err => console.error("❌ [SIGNIN] Background schema assignment error:", err));
      }

      const accessToken = session?.access_token || null;
      // Public included: the backend keeps only presence pings for public.
      if (accessToken && activeSchema) {
        setAuthContext({ token: accessToken, schema: activeSchema });
        trackUserEvent('session_start', {
          metadata: { account_type: accountType },
        });
        flushUserEvents();
      } else if (accessToken && activeSchema === 'public') {
        console.warn('[user_events_debug] skipping setAuthContext — user is on public schema (no tenant assigned)');
      }

      console.log("🚀 [SIGNIN] Redirecting to /schedules...");
      const justSignedUp = sessionStorage.getItem("auth_just_signed_up");
      sessionStorage.removeItem("auth_just_signed_up");
      if (justSignedUp) {
        alert("Welcome!\n\nYour account has been created. Please wait for an admin to grant you access.");
      } else {
        alert(`User signed in: ${user.user_metadata?.username || user.email}`);
      }
      window.location.href = window.location.origin + (basename || "") + "/schedules";
    };

    const {
      data: { subscription },
    } = supabasePublic().auth.onAuthStateChange((event, session) => {
      console.log("🔐 [AUTH] Auth state changed:", event);

      if (event === "SIGNED_IN") {
        setTimeout(() => {
          // Defer session lookup out of onAuthStateChange to avoid the
          // Supabase auth lock. Also covers OAuth and magic-link sessions.
          recordAuthEvent("login", { success: true });
          handleSignedIn(session).catch((err) => {
            console.error("💥 [SIGNIN] Error handling signed-in session:", err);
          });
        }, 0);
      }
    });

    return () => {
      cancelled = true;
      subscription.unsubscribe();
    };
  }, [navigate]);

  const handleSignUp = async (e) => {
    e.preventDefault();
    setError("");
    const derivedPatientName = patientDisplayName(patientFields);
    const signupName = isPatientSignup ? derivedPatientName : username.trim();

    if (!isPatientSignup && !username.trim()) {
      setError("Username is required.");
      return;
    }
    if (!email.trim()) {
      setError("Email is required.");
      return;
    }
    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email.trim())) {
      setError("Please enter a valid email address.");
      return;
    }
    if (!password) {
      setError("Password is required.");
      return;
    }
    if (password.length < 6) {
      setError("Password must be at least 6 characters.");
      return;
    }

    if (!isPatientSignup && !signupReason.trim()) {
      setError("Please explain how you will use the app.");
      return;
    }
    const patientValidation = isPatientSignup
      ? validatePatientSignupFields(patientFields, email.trim())
      : null;
    if (patientValidation) {
      setError(patientValidation);
      return;
    }
    if (!isPatientSignup && referralNeedsDetail && !referralDetail.trim()) {
      setError("Please tell us how you heard about the app.");
      return;
    }

    setLoading(true);
    try {
      const signUpEmail = email.trim();
      const patientSignupReason = isPatientSignup
        ? null
        : (signupReason.trim() + ' ||| ' + referralSource + (referralNeedsDetail ? ': ' + referralDetail.trim() : '')) || null;
      // Set before signUp() so when SIGNED_IN fires (same tick or shortly after), onAuthStateChange sees it
      sessionStorage.setItem("auth_just_signed_up", "true");
      if (isPatientSignup) {
        sessionStorage.setItem("patient_signup_backend_pending", "true");
      }

      console.log("🚀 [SIGNUP] Starting signup process...", {
        email: signUpEmail,
        username: signupName,
        account_type: accountType,
        has_signup_reason: !!signupReason.trim(),
      });

      // Store account type and signup reason in user metadata for later use
      const { data, error: signUpError } = await supabasePublic().auth.signUp({
        email: signUpEmail,
        password,
        options: {
          emailRedirectTo: 'https://notesai-c.onrender.com/NotesAI/confirm',
          data: {
            username: signupName,
            full_name: signupName,
            patient_first_name: isPatientSignup ? patientFields.patientFirstName.trim() : null,
            patient_middle_name: isPatientSignup ? patientFields.patientMiddleName.trim() : null,
            patient_last_name: isPatientSignup ? patientFields.patientLastName.trim() : null,
            patient_dob: isPatientSignup ? patientFields.patientDob : null,
            account_type: accountType,
            signup_reason: patientSignupReason,
          },
        },
      });

      console.log("📬 [SIGNUP] Supabase response received:", {
        has_error: !!signUpError,
        error: signUpError,
        has_user: !!data?.user,
        has_session: !!data?.session,
        user_id: data?.user?.id,
        email_confirmed: data?.user?.email_confirmed_at,
        confirmation_sent_at: data?.user?.confirmation_sent_at,
      });

      if (signUpError) {
        console.error("❌ [SIGNUP] Signup error:", signUpError);
        sessionStorage.removeItem("auth_just_signed_up");
        sessionStorage.removeItem("patient_signup_backend_pending");
        setError(signUpError.message);
        return;
      }

      console.log("✅ [SIGNUP] User signup initiated successfully:", {
        email: signUpEmail,
        username: signupName,
        account_type: accountType,
        user_id: data.user?.id,
        confirmation_sent_at: data.user?.confirmation_sent_at,
        session_exists: !!data.session,
      });

      const userId = data.user?.id;
      const signupPayload = buildUnauthenticatedSignupPayload({
        userId,
        email: signUpEmail,
        name: signupName,
        accountType,
        signupReason: patientSignupReason,
        patientFields,
      });
      const shouldCreatePatientRecordBeforeRedirect = isPatientSignup && !!data.session;
      const shouldCreateUnauthenticatedUser = !data.session || shouldCreatePatientRecordBeforeRedirect;
      let createUserResult = null;

      // Patient signups must always send patient_fields before verification, even when a session exists immediately.
      if (shouldCreateUnauthenticatedUser) {
        console.log("🔧 [SIGNUP] Creating signup user record via backend...", {
          user_id: userId,
          email: signUpEmail,
          name: signupName,
          account_type: accountType,
          immediate_session: !!data.session,
        });

        try {
          // Use the shared resolver (same-origin in prod, localhost:8001 in dev)
          // — the same helper lines 130/272 already use. This previously hardcoded
          // https://notesai-c.onrender.com, which made signup a CROSS-ORIGIN call
          // to a different Render service even when served from
          // app.magicnotesai.com — the cause of the create-unauthenticated-user
          // CORS preflight failures. Same-origin needs no CORS at all.
          const backendUrl = getBackendURL();

          const createUserResponse = await fetch(`${backendUrl}/api/create-unauthenticated-user/`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(signupPayload),
          });

          if (!createUserResponse.ok) {
            const errorData = await createUserResponse.json().catch(() => ({}));
            console.error("❌ [SIGNUP] Error creating user record:", errorData);
            sessionStorage.removeItem("auth_just_signed_up");
            sessionStorage.removeItem("patient_signup_backend_pending");
            if (createUserResponse.status === 409 || errorData.code === "account_exists") {
              // Duplicate account — an account with this email already exists.
              setError(errorData.error || "An account with this email already exists. Please log in instead.");
              setIsSignUp(false); // flip to the sign-in view
            } else {
              setError(`Account created but error saving user data: ${errorData.error || 'Unknown error'}`);
            }
            return;
          }

          createUserResult = await createUserResponse.json();
          console.log("✅ [SIGNUP] User record created successfully with unauthenticated role:", createUserResult);
          sessionStorage.removeItem("patient_signup_backend_pending");
        } catch (userCreateError) {
          console.error("💥 [SIGNUP] Exception creating user:", userCreateError);
          sessionStorage.removeItem("auth_just_signed_up");
          sessionStorage.removeItem("patient_signup_backend_pending");
          setError(`Account created but error saving user data: ${userCreateError.message}`);
          return;
        }
      } else {
        console.log("🔓 [SIGNUP] Email confirmation disabled - skipping unauthenticated user creation (onAuthStateChange will handle it)");
        console.log("🔓 [SIGNUP] onAuthStateChange will fire SIGNED_IN event and redirect user");
        return; // Exit early - onAuthStateChange will handle everything (auth_just_signed_up already set above)
      }

      if (shouldCreatePatientRecordBeforeRedirect) {
        const patientRedirectPath = createUserResult?.patient_match_status === "verified"
          ? "/schedules"
          : "/auth/patient-verification";
        window.location.href = window.location.origin + (basename || "") + patientRedirectPath;
        return;
      }

      // Check if email confirmation is enabled
      if (!data.session) {
        console.log("📧 [SIGNUP] No session - email confirmation required");
        console.log("📧 [SIGNUP] User should receive email at:", signUpEmail);
        console.log("📧 [SIGNUP] confirmation_sent_at timestamp:", data.user?.confirmation_sent_at);
        sessionStorage.removeItem("auth_just_signed_up");
        sessionStorage.removeItem("patient_signup_backend_pending");

        // Show success message instead of error
        alert(`✅ Account created successfully!\n\nPlease check your email (${signUpEmail}) for a confirmation link to activate your account.\n\nAfter confirming your email, you can sign in.\n\n⚠️ Check your spam folder if you don't see it!`);

        // Switch back to sign-in view
        setIsSignUp(false);
        setEmail("");
        setPassword("");
        setUsername("");
        setPatientFields(emptyPatientSignupFields);
        setSignupReason("");
        setReferralSource("Instagram");
        setReferralDetail("");
        console.log("✅ [SIGNUP] Switched to sign-in view");
      } else {
        console.log("🔓 [SIGNUP] Session created immediately - email confirmation disabled");
        // Email confirmation disabled - user is signed in immediately
        // onAuthStateChange will handle the redirect
      }
    } catch (err) {
      console.error("💥 [SIGNUP] Exception during signup:", err);
      sessionStorage.removeItem("patient_signup_backend_pending");
      setError(err.message);
    } finally {
      setLoading(false);
      console.log("🏁 [SIGNUP] Signup process completed");
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setResetError("");
    setResetMessage("");

    if (!resetEmail.trim()) {
      setResetError("Please enter your email address.");
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(resetEmail.trim())) {
      setResetError("Please enter a valid email address.");
      return;
    }

    setLoading(true);
    try {
      console.log("🔑 [RESET] Sending password reset email to:", resetEmail.trim());

      const { error } = await supabasePublic().auth.resetPasswordForEmail(
        resetEmail.trim(),
        {
          redirectTo: `${window.location.origin}${basename || ""}/auth/reset-password`,
        }
      );

      if (error) {
        console.error("❌ [RESET] Error sending reset email:", error);
        setResetError(error.message);
        return;
      }

      console.log("✅ [RESET] Password reset email sent successfully");
      setResetMessage(`Password reset email sent to ${resetEmail.trim()}. Please check your inbox and spam folder.`);

      // Clear form and close modal after 3 seconds
      setTimeout(() => {
        setShowForgotPassword(false);
        setResetEmail("");
        setResetMessage("");
      }, 3000);
    } catch (err) {
      console.error("💥 [RESET] Exception during password reset:", err);
      setResetError(err.message);
    } finally {
      setLoading(false);
      console.log("🏁 [RESET] Password reset process completed");
    }
  };

  // Custom sign-up form
  if (isSignUp) {
    return (
      <div style={{ borderRadius: "8px", padding: "20px", maxWidth: "400px", margin: "0 auto" }}>
        <form onSubmit={handleSignUp}>
          {!isPatientSignup && (
            <div style={{ marginBottom: "12px" }}>
              <label style={labelStyle}>Username *</label>
              <input
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                placeholder="Choose a username"
                style={inputStyle}
              />
            </div>
          )}

          <div style={{ marginBottom: "12px" }}>
            <label style={labelStyle}>Email *</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="your@email.com"
              style={inputStyle}
              required
            />
          </div>

          <div style={{ marginBottom: "12px" }}>
            <label style={labelStyle}>Account Type *</label>
            <select
              value={accountType}
              onChange={(e) => setAccountType(e.target.value)}
              style={inputStyle}
            >
              <option value="doctor">Doctor / Clinician</option>
              <option value="patient">Patient</option>
              <option value="student">Student</option>
              <option value="medstudent">Medical Student</option>
              <option value="socialworker">Social Worker</option>
              <option value="other">Other / Explain Below</option>
            </select>
          </div>

          {isPatientSignup && (
            <>
              <div style={{ marginBottom: "12px" }}>
                <label style={labelStyle}>First name *</label>
                <input
                  type="text"
                  value={patientFields.patientFirstName}
                  onChange={(e) => setPatientFields((prev) => ({ ...prev, patientFirstName: e.target.value }))}
                  placeholder="First name on file"
                  style={inputStyle}
                />
              </div>
              <div style={{ marginBottom: "12px" }}>
                <label style={labelStyle}>Middle name</label>
                <input
                  type="text"
                  value={patientFields.patientMiddleName}
                  onChange={(e) => setPatientFields((prev) => ({ ...prev, patientMiddleName: e.target.value }))}
                  placeholder="Middle name on file (optional)"
                  style={inputStyle}
                />
              </div>
              <div style={{ marginBottom: "12px" }}>
                <label style={labelStyle}>Last name *</label>
                <input
                  type="text"
                  value={patientFields.patientLastName}
                  onChange={(e) => setPatientFields((prev) => ({ ...prev, patientLastName: e.target.value }))}
                  placeholder="Last name on file"
                  style={inputStyle}
                />
              </div>
              <div style={{ marginBottom: "12px" }}>
                <label style={labelStyle}>Date of birth *</label>
                <input
                  type="date"
                  value={patientFields.patientDob}
                  onChange={(e) => setPatientFields((prev) => ({ ...prev, patientDob: e.target.value }))}
                  style={inputStyle}
                />
              </div>
              <div style={{ marginBottom: "12px" }}>
                <label style={labelStyle}>Phone number *</label>
                <input
                  type="tel"
                  value={patientFields.patientPhone}
                  onChange={(e) => setPatientFields((prev) => ({ ...prev, patientPhone: e.target.value }))}
                  placeholder="Phone number on file"
                  style={inputStyle}
                />
              </div>
            </>
          )}

          {!isPatientSignup && (
            <>
              <div style={{ marginBottom: "12px" }}>
                <label style={labelStyle}>
                  How will you use the app? *
                </label>
                <textarea
                  value={signupReason}
                  onChange={(e) => setSignupReason(e.target.value)}
                  placeholder="Please explain how you'll use the app (required)"
                  style={{
                    ...inputStyle,
                    minHeight: "80px",
                    resize: "vertical",
                    fontFamily: "inherit",
                  }}
                  required
                />
              </div>

              {lockedReferralName ? (
                <div style={{ marginBottom: "12px" }}>
                  <label style={labelStyle}>How did you hear about us?</label>
                  <div style={{ ...inputStyle, backgroundColor: "#f5f5f5", color: "#555", cursor: "not-allowed" }}>
                    Referred by: {lockedReferralName}
                  </div>
                </div>
              ) : (
                <>
                  <div style={{ marginBottom: "12px" }}>
                    <label style={labelStyle}>How did you hear about us? *</label>
                    <select
                      value={referralSource}
                      onChange={(e) => { setReferralSource(e.target.value); setReferralDetail(""); }}
                      style={inputStyle}
                    >
                      <option value="Instagram">Instagram</option>
                      <option value="TikTok">TikTok</option>
                      <option value="a friend">A friend</option>
                      <option value="other">Other</option>
                    </select>
                  </div>
                  {referralNeedsDetail && (
                    <div style={{ marginBottom: "12px" }}>
                      <label style={labelStyle}>
                        {referralSource === "a friend" ? "Who referred you? *" : "Please specify *"}
                      </label>
                      <input
                        type="text"
                        value={referralDetail}
                        onChange={(e) => setReferralDetail(e.target.value)}
                        placeholder={referralSource === "a friend" ? "Friend's name or details" : "How did you hear about us?"}
                        style={inputStyle}
                      />
                    </div>
                  )}
                </>
              )}
            </>
          )}

          <div style={{ marginBottom: "16px" }}>
            <label style={labelStyle}>Password *</label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="At least 6 characters"
              style={inputStyle}
            />
          </div>

          {error && (
            <p style={{ color: "red", marginBottom: "12px", fontSize: "14px" }}>{error}</p>
          )}

          <div style={{ marginBottom: "12px" }}>
            <button
              type="button"
              onClick={async () => {
                setError("");
                try {
                  sessionStorage.setItem("auth_google_signup", "true");
                  if (lockedReferralName) sessionStorage.setItem("auth_referral_ref", lockedReferralName);
                  const redirectURL = getRedirectURL();
                  console.log("🔐 [GOOGLE OAUTH] Initiating sign-in", {
                    redirectTo: redirectURL,
                    currentOrigin: window.location.origin,
                    currentHref: window.location.href,
                  });
                  const { data: oauthData, error: oauthError } = await supabasePublic().auth.signInWithOAuth({
                    provider: "google",
                    options: { redirectTo: redirectURL },
                  });
                  console.log("🔐 [GOOGLE OAUTH] signInWithOAuth response", { data: oauthData, error: oauthError });
                  if (oauthError) {
                    recordAuthEvent("login", { success: false });
                    setError(oauthError.message);
                  }
                } catch (e) {
                  recordAuthEvent("login", { success: false });
                  setError(e.message || "Google sign-in failed");
                }
              }}
              style={{
                width: "100%",
                backgroundColor: "#fff",
                borderRadius: "12px",
                padding: "10px",
                fontWeight: "bold",
                color: "#333",
                border: "1px solid #ddd",
                fontSize: "16px",
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: "8px",
              }}
            >
              <svg width="18" height="18" viewBox="0 0 18 18">
                <path fill="#4285F4" d="M16.51 8H8.98v3h4.3c-.18 1-.74 1.48-1.6 2.04v2.01h2.6a7.8 7.8 0 0 0 2.38-5.88c0-.57-.05-.66-.15-1.18z"/>
                <path fill="#34A853" d="M8.98 17c2.16 0 3.97-.72 5.3-1.94l-2.6-2a4.8 4.8 0 0 1-2.7.8c-2.07 0-3.83-1.4-4.46-3.42H1.83v2.07A8 8 0 0 0 8.98 17z"/>
                <path fill="#FBBC05" d="M4.52 10.05a4.8 4.8 0 0 1 0-3.07V4.91H1.83a8 8 0 0 0 0 7.18l2.69-2.04z"/>
                <path fill="#EA4335" d="M8.98 4.58c1.17 0 2.23.4 3.06 1.2l2.3-2.3A8 8 0 0 0 1.83 4.9l2.69 2.07c.63-2.02 2.39-3.42 4.46-3.42z"/>
              </svg>
              Sign up with Google
            </button>
            <p style={{ textAlign: "center", marginTop: "8px", fontSize: "12px", color: "#666" }}>
              You will still choose your role and complete signup details after signing in with Google.
            </p>
          </div>

          <div style={{ marginBottom: "8px", textAlign: "center", color: "#999" }}>— or —</div>

          <button
            type="submit"
            disabled={loading}
            style={{
              width: "100%",
              backgroundColor: "#3b5998",
              borderRadius: "12px",
              padding: "10px",
              fontWeight: "bold",
              color: "#fff",
              border: "none",
              fontSize: "16px",
              cursor: loading ? "not-allowed" : "pointer",
              opacity: loading ? 0.7 : 1,
            }}
          >
            {loading ? "Signing up..." : "Sign Up"}
          </button>
        </form>

        <p style={{ textAlign: "center", marginTop: "16px", fontSize: "14px" }}>
          Already have an account?{" "}
          <span
            onClick={() => setIsSignUp(false)}
            style={{ color: "#4A90E2", cursor: "pointer", textDecoration: "underline" }}
          >
            Sign In
          </span>
        </p>

        <div style={{ marginTop: "20px", textAlign: "center" }}>
          <HelpDeskButton size="xs" navigateToPage />
        </div>
      </div>
    );
  }

  // Default: Supabase Auth UI for sign-in
  return (
    <div>
      <Auth
        supabaseClient={signInClient}
        providers={["google"]}
        view="sign_in"
        redirectTo={getRedirectURL()}
        appearance={customAppearance}
        showLinks={false}
        localization={{
          variables: {
            sign_in: {
              email_label: "Email address or Username",
              email_input_placeholder: "your email or username",
            },
          },
        }}
      />

      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: "16px",
          marginTop: "12px",
          fontSize: "14px",
          flexWrap: "wrap",
        }}
      >
        <button
          onClick={() => setShowForgotPassword(true)}
          style={{
            background: "none",
            border: "none",
            color: "#4A90E2",
            cursor: "pointer",
            textDecoration: "underline",
            fontSize: "14px",
            padding: "4px",
          }}
        >
          Forgot Password?
        </button>
        <span style={{ color: "#999" }}>|</span>
        <span>
          Don't have an account?{" "}
          <span
            onClick={() => setIsSignUp(true)}
            style={{ color: "#4A90E2", cursor: "pointer", textDecoration: "underline" }}
          >
            Sign Up
          </span>
        </span>
      </div>

      <div style={{ marginTop: "20px", textAlign: "center" }}>
        <HelpDeskButton size="xs" navigateToPage />
      </div>

      {/* Forgot Password Modal */}
      {showForgotPassword && (
        <div style={{
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: "rgba(0, 0, 0, 0.5)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          zIndex: 1000,
        }}>
          <div style={{
            backgroundColor: "white",
            borderRadius: "12px",
            padding: "30px",
            maxWidth: "400px",
            width: "90%",
            boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
          }}>
            <h2 style={{ marginTop: 0, marginBottom: "20px", fontSize: "24px", color: "#333" }}>
              Reset Password
            </h2>

            <form onSubmit={handleForgotPassword}>
              <div style={{ marginBottom: "16px" }}>
                <label style={labelStyle}>Email Address *</label>
                <input
                  type="email"
                  value={resetEmail}
                  onChange={(e) => setResetEmail(e.target.value)}
                  placeholder="your@email.com"
                  style={inputStyle}
                  autoFocus
                />
              </div>

              {resetError && (
                <p style={{ color: "red", marginBottom: "12px", fontSize: "14px" }}>
                  {resetError}
                </p>
              )}

              {resetMessage && (
                <p style={{ color: "green", marginBottom: "12px", fontSize: "14px" }}>
                  {resetMessage}
                </p>
              )}

              <div style={{ display: "flex", gap: "10px" }}>
                <button
                  type="submit"
                  disabled={loading}
                  style={{
                    flex: 1,
                    backgroundColor: "#3b5998",
                    borderRadius: "12px",
                    padding: "10px",
                    fontWeight: "bold",
                    color: "#fff",
                    border: "none",
                    fontSize: "16px",
                    cursor: loading ? "not-allowed" : "pointer",
                    opacity: loading ? 0.7 : 1,
                  }}
                >
                  {loading ? "Sending..." : "Send Reset Link"}
                </button>

                <button
                  type="button"
                  onClick={() => {
                    setShowForgotPassword(false);
                    setResetEmail("");
                    setResetError("");
                    setResetMessage("");
                  }}
                  disabled={loading}
                  style={{
                    flex: 1,
                    backgroundColor: "#f5f5f5",
                    borderRadius: "12px",
                    padding: "10px",
                    fontWeight: "bold",
                    color: "#333",
                    border: "1px solid #ddd",
                    fontSize: "16px",
                    cursor: loading ? "not-allowed" : "pointer",
                    opacity: loading ? 0.7 : 1,
                  }}
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AuthUi;
